/**
 * Data layer for `campaign-geo-targeting`. Thin typed fetch wrappers over /api/v1/campaign-geo-targeting.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CampaignGeoTargetingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCampaignGeoTargetingResponse {
	count: number;
	items: CampaignGeoTargetingRow[];
}

export interface CreateCampaignGeoTargetingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/campaign-geo-targeting";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCampaignGeoTargetingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCampaignGeoTargetingResponse>(res);
}

export async function create(body: CreateCampaignGeoTargetingBody): Promise<CampaignGeoTargetingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CampaignGeoTargetingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const campaignGeoTargetingQueryKeys = {
	all: ["campaign-geo-targeting"] as const,
	list: () => [...campaignGeoTargetingQueryKeys.all] as const,
	detail: (id: string) => [...campaignGeoTargetingQueryKeys.all, id] as const,
};
